<template>
  <div class="tab-pane show active" id="introduction">
    <h3 class="info-text mb-0">Migration Introduction</h3>
    <p class="text-center text-secondary mb-4">
      <em>Please take a moment and read the below information so you may familiarize yourself with our migration process</em>
    </p>
    <div class="row justify-content-left">
      <div class="col-12 pr-5 pl-5">
        <h5>The Basics</h5>
        <p>Since every website is different, each requires its own set of considerations to ensure an absolutely seamless migration. Please take care to submit accurate login information and if you have any questions at all, please be sure to ask!
          <br><br> An SSL (https) will be included automatically on every website we migrate.
        </p>

        <h5>Expectations</h5>
        <p>There will be no expected downtime during this migration to BigScoots. However, because there are a lot of moving parts and hundreds of variables that always differ from site to site, it is very important that once the migration is completed to inform us of any small irregularities. We triple check our work and migrate your site(s) as if they were our own, but there is always a very small chance of an item being out of place – please simply let us know if this is the case and we’ll provide a fix immediately. </p>

        <h5>What Happens Next?</h5>
        <p>Once this migration request is submitted, our BigScoots team will schedule in your migration as soon as possible. For this reason, it is recommended to not make any changes to your site during this time. If you must however for any reason at all, that won’t be a problem. Please simply be sure to let us know so we can resync your site as the migration finishes, to ensure we have the most up to date version possible.
          <br><br> We never modify your original website at the current host before, during or after a migration. It will continue to work as usual and we will always notify you whenever a migration is completed. It is recommended to wait 48 hours after the migration is completed before canceling your service with your old host.
        </p>
        <hr>

        <h5>Choose your plan:</h5>
        <div class="row">
          <div class="col-sm-12 col-md-6">
            <div class="form-group">
              <label for="planSelection">
                Please select the plan you wish to migrate your site(s) to
                <strong class="text-danger">*</strong>
              </label>
              <select class="form-control form-control-sm" id="planSelection" name="planSelection" v-model="pIndex" @change="checkValid" required style="min-height:3em;">
                <option value="">Choose your Plan</option>
                <option v-for="(plan, index) in hostingPlans" :key="index" :value="plan.planIndex">{{ plan.planName }}</option>
              </select>
            </div>
          </div>
          <div class="col-sm-12 col-md-6">
            <strong class="text-danger mr-4" style="vertical-align: sub; font-size: 1.4em;">OR</strong>
            <button type="button" class="btn btn-primary btn-round mt-4 align-baseline" data-toggle="modal" data-target="#addNewPlan" @click="fixModal">
              <i class="fa fa-plus mr-2"></i>
              Add New Managed WP Service
            </button>
            <!-- Modal for adding new WP Service Plan - Start -->
            <div class="add-new-plan-modal position-relative">
              <div class="modal fade" id="addNewPlan" tabindex="-1" role="dialog" aria-labelledby="addNewPlanTitle">
                <div class="modal-dialog" role="document">
                  <div class="modal-content">
                    <div class="modal-header">
                      <h5 class="modal-title" id="addNewPlanTitle">Add New Managed WP Service</h5>
                      <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div class="modal-body">
<!--                      <div class="form-group">-->
<!--                        <label for="newPlanDomain">Primary domain name for the new plan-->
<!--                          <strong class="text-danger">*</strong>-->
<!--                        </label>-->
<!--                        <input type="text" class="form-control" id="newPlanDomain" aria-describedby="newPlanDomain" placeholder="Example: domain.com" required @input="newPlanValid" v-model.trim="addNewPlan.domain">-->
<!--                      </div>-->
                      <div class="form-group">
                        <label for="newPlanName">Select your new plan
                          <strong class="text-danger">*</strong>
                        </label>
                        <select class="form-control" id="newPlanName" required style="min-height: 3em;" v-model="addNewPlan.planName" @change="newPlanValid">
                          <option value="">Choose a plan</option>
                          <option value="WPO Starter">WPO Starter - $34.95 USD</option>
                          <option value="WPO Professional">WPO Professional - $98.95 USD</option>
                          <option value="WPO Business">WPO Business - $248.95 USD</option>
                          <option value="WPO Enterprise">WPO Enterprise - Contact Us</option>
                        </select>
                        <small id="newPlanName" class="form-text text-muted">Please check out our
                          <a href="https://www.bigscoots.com/wordpress-optimized-hosting/" target="_blank">Managed WordPress Page</a> for additional details or
                          <a href="https://bigscoots.com/contact/" target="_blank">Contact Us</a> for help!</small>
                      </div>
                      <div class="alert border border-primary round-border text-dark mt-4" role="alert">
                        <h4 class="alert-heading text-primary mt-0 mb-2">
                          <i class="fa fa-info-circle"></i> Please Note</h4>
                        <p>By submitting this request, you are giving BigScoots permission to add a new Managed WordPress Service onto your account. It will first be added as a temporary domain while we work hard to setup and optimize your new service. Please feel free during this time to continue through the migration request form so we can get started on your migration right away! You can expect an email from us very shortly confirming your new billing and login details.</p>
                      </div>
                    </div>
                    <div class="modal-footer">
                      <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
                      <button type="button" class="btn btn-primary" data-dismiss="modal" :disabled="addNewPlan.planName === ''" @click="addNewPlanDB">Add New Plan</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Modal for adding new WP Service Plan - End -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import swal from "sweetalert2";
import { eventBus } from "@/main";
import { db } from "@/firebase/init";
import assignDomainMixin from "../../../../mixins/assignDomainMixin";

export default {
  name: "RequestMigrationIntroduction",
  mixins: [assignDomainMixin],
  props: {
    planIndex: Number,
    collaboration: {
      type: Boolean,
      default() { return false; }
    },
    userUID: {
      type: String,
      default() { return ""; }
    },
    supervisor: {
      type: Object,
      default() {return {}}
    }
  },
  data() {
    return {
      pIndex: typeof this.planIndex === "undefined" ? "" : this.planIndex,
      addNewPlan: {
        domain: null,
        planName: ""
      }
    };
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    clientID() {
      return this.$store.getters["getRequestCustomerID"];
    },
    accounts() {
      return this.$store.getters["hosting/accounts"];
    },
    hostingPlans() {
      return this.$store.getters["hosting/plans"];
    },
    newPlanStructure() {
      return this.$store.getters["hosting/newPlanStructure"];
    },
    migrationCount() {
      return this.$store.getters["migration/count"];
    },
    plans() {
      return this.$store.getters["plan/list"];
    }
  },
  methods: {
    checkValid() {
      /* eslint-disable */
      $(".card-wizard form").valid();
      /* eslint-enable */
    },
    newPlanValid() {
      /* eslint-disable */
      // $("#newPlanDomain").valid();
      $("#newPlanName").valid();
      /* eslint-enable */
    },
    fixModal() {
      /* eslint-disable */
      $(document).ready(function() {
        $(".modal-backdrop").appendTo(".add-new-plan-modal");
      });
      /* eslint-enable */
    },
      planLimitCountGenerator(planName) {
          let plan = this.plans.filter((item)=>item.name == planName);
          if(plan.length) {
              return plan[0].planLimit;
          }
          return 1;
      },
      diskSpaceGenerator(planName) {
          let plan = this.plans.filter((item)=>item.name == planName);
          if(plan.length) {
              return plan[0].maxStorageSize;
          }
          return 10;
      },
      visitorsGenerator(planName) {
          let plan = this.plans.filter((item)=>item.name == planName);
          if(plan.length) {
              return plan[0].visitors;
          }
          return 250000;
      },
    async addNewPlanDB() {
      // Check if we have the new plan's domain name and plan name
      if (
        this.addNewPlan.planName !== ""
      ) {
        // Set asyncDone to false
        this.$store.dispatch("async/stateUpdate", { state: false });

        await db.collection('bigScootsTasks').add({
          createdAt: new Date().toISOString(),
          status: 'pending',
          type: 'sales',
          text: `New Plan Request`,
          email: (this.collaboration) ? this.supervisor.supervisorEmail : this.user.email,
          name: (this.collaboration) ? this.supervisor.supervisorName : this.user.firstName + ' ' + this.user.lastName,
          domain: this.addNewPlan.planName,
        });

        // Reset the data
        this.addNewPlan.domain = null;
        this.addNewPlan.planName = "";

        // Set asyncDone to true
        this.$store.dispatch("async/stateUpdate", { state: true });

        // Show success message to the user that the new plan has been added with pending status
        swal({
          title: "Success!",
          text: 'New plan has been successfully requested to bigScoots team!',
          type: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#6ACF98"
        });
        // Connect with firebase cloud function, passing all the required data and performing both ticket generation and database update from the server
        // const addNewPlan = functions.httpsCallable("addNewPlan");
        //
        // addNewPlan({
        //   clientID: this.clientID,
        //   planName: this.addNewPlan.planName,
        //   cc: (this.collaboration) ? this.user.email : '',
        //   submitterName: this.user.firstName + ' ' + this.user.lastName
        // })
        //   .then(async result => {
        //     if (result.data.success === true) {
        //
        //       // Reset the data
        //       this.addNewPlan.domain = null;
        //       this.addNewPlan.planName = "";
        //
        //       // Set asyncDone to true
        //       this.$store.dispatch("async/stateUpdate", { state: true });
        //
        //       // Show success message to the user that the new plan has been added with pending status
        //       swal({
        //         title: "New Plan Added!",
        //         text: result.data.message,
        //         type: "success",
        //         confirmButtonText: "OK",
        //         confirmButtonColor: "#6ACF98"
        //       });
        //     } else {
        //       // Set asyncDone to true
        //       this.$store.dispatch("async/stateUpdate", { state: true });
        //
        //       // Show error message to the user that the new plan has been added with pending status
        //       swal({
        //         title: "Something went wrong!",
        //         text: result.data.message,
        //         type: "error",
        //         confirmButtonText: "Try Again!",
        //         confirmButtonColor: "#EE8057"
        //       });
        //     }
        //   })
        //   .catch(err => {
        //     // Set asyncDone to true
        //     this.$store.dispatch("async/stateUpdate", { state: true });
        //       this.addLog({
        //           message: "Error: Error add New Plan to DB. Message:" + err.message,
        //           level:  "danger"
        //       });
        //     // Show error message to the user that the new plan has been added with pending status
        //     swal({
        //       title: "Something went wrong!",
        //       text: err.message,
        //       type: "error",
        //       confirmButtonText: "Try Again!",
        //       confirmButtonColor: "#EE8057"
        //     });
        //   });
      } else {
        swal({
          title: "All Fields are Required!",
          text: "Please fill all fields when adding a new plan. All fields are required.",
          type: "error",
          confirmButtonText: "Try Again!",
          confirmButtonColor: "#EE8057"
        });
      }
    }
  },
  created() {
    if (!this.collaboration && this.$store.getters["hosting/accounts"].length === 0) {
      this.$store.dispatch("hosting/fetchAccounts");
    }
    eventBus.$off("nextPage")
    eventBus.$on("nextPage", data => {
      // Only execute this only when the page is successfully going from introduction to site details page
      if (data.page === 'introduction') {
        if (this.pIndex !== "") {
          if (this.accounts[this.pIndex].primary.domain !== '') {
            this.$store.dispatch("migration/planIndexUpdate", {
              index: this.pIndex
            });
            if(this.collaboration){
              this.$store.dispatch("migration/countUpdateDB", {UID: this.userUID});
            }else{
              this.$store.dispatch("migration/countUpdateDB");
            }
          } else {
            this.$router.push({
             name: 'addSite',
             query: {toMigrate: 0, planIndex: this.pIndex}
            })
          }
        }
      }
    });
  }
};
</script>
